import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { IndicationUnit } from "./IndicationUnit";

export type StatusPillIndicator = "success" | "pending" | "error" | "disabled";

/** Variants supported by both `<Text>` and `<IndicationUnit pairWith=...>`,
 *  i.e. the intersection that pairs cleanly with a status dot. */
export type StatusPillTextVariant =
    | "hero"
    | "h1"
    | "number-lg"
    | "number"
    | "body"
    | "small"
    | "fine-print"
    | "label";

interface StatusPillProps {
    /** Drives the IndicationUnit dot's color (matches the
     *  IndicationUnit's own StatusState — `success` / `pending` /
     *  `error` / `disabled`). */
    indicatorState: StatusPillIndicator;
    /** Pill label text — typically a single word like "Earning",
     *  "Approved", "Disabled". */
    children: React.ReactNode;
    /** Tailwind class for the text color. The caller chooses based
     *  on intent — typical pairings:
     *
     *    success  → "text-success-base"
     *    pending  → "text-warning-base"
     *    error    → "text-error-base"
     *    disabled → "text-foreground-muted" or similar
     *
     *  Left intentionally as a className rather than a semantic enum
     *  so callers can pick any token that fits their surface (e.g.,
     *  yaas-issuer's pending pills may use a different color than
     *  retail's). */
    textClassName?: string;
    /** Text variant from the design system (small, body, label,
     *  etc.). The IndicationUnit auto-sizes its dot to pair with
     *  this variant. Defaults to "small". */
    textVariant?: StatusPillTextVariant;
}

/**
 * Generic status pill — colored dot + label, inline-flex. Used
 * across YaaS retail, YaaS consoles, metrics dashboards, etc.,
 * wherever a position / record / connection has a categorical state
 * that needs to read at a glance.
 *
 * Surface-specific labels (e.g. "Earning" / "Pending" / "Disabled"
 * for retail YaaS positions; "Live" / "Pending" / "Rejected" for
 * yaas-issuer applications) live in thin wrapper components that
 * compose this primitive with their own state→pair mappings.
 */
export function StatusPill({
    indicatorState,
    children,
    textClassName,
    textVariant = "small"
}: StatusPillProps): React.ReactElement {
    return (
        <span className="inline-flex items-center gap-1.5">
            <IndicationUnit
                variant="status"
                state={indicatorState}
                pairWith={textVariant}
            />
            <Text
                variant={textVariant}
                render={<span />}
                className={textClassName}
            >
                {children}
            </Text>
        </span>
    );
}
